/**
 * MarketState — small read-out panel of global market posture.
 *
 * Two coloured "phase" + "stress" chips, then a short list of
 * numbers. The data comes from the dashboard endpoint cached for ~20s.
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { formatPct, formatUsd } from '@/lib/format'
import type { MarketState as MarketStateData } from '@/hooks/useDashboardApi'

const PHASE_TONE: Record<string, string> = {
  bullish:       'text-pulse',
  recovery:      'text-sky',
  consolidation: 'text-textDim',
  bearish:       'text-rose',
  capitulation:  'text-rose',
}

const STRESS_TONE: Record<string, string> = {
  normal:   'text-pulse',
  elevated: 'text-amber',
  critical: 'text-rose',
}

interface MarketStateProps {
  data: MarketStateData | undefined
  loading?: boolean
}

export function MarketState({ data, loading }: MarketStateProps) {
  const { t } = useTranslation('app')

  const phase = data?.btc_phase ?? 'unknown'
  const stress = data?.stress_level ?? 'normal'

  return (
    <section className="bg-ink p-5">
      <header className="flex items-baseline mb-4">
        <h3 className="text-sm font-semibold">{t('dashboard.market.title')}</h3>
      </header>

      {loading ? (
        <div className="text-textFaint text-sm">—</div>
      ) : (
        <dl className="space-y-3 text-sm">
          <Row label={t('dashboard.market.btc_phase')}>
            <span className={cn('font-medium', PHASE_TONE[phase] ?? 'text-textDim')}>
              {t(`dashboard.market.phase.${phase}`, {
                defaultValue: t('dashboard.market.phase.unknown') as string,
              })}
            </span>
          </Row>
          <Row label={t('dashboard.market.stress')}>
            <span className={cn('font-medium', STRESS_TONE[stress])}>
              {t(`dashboard.market.stress_level.${stress}`)}
            </span>
          </Row>
          <Row label={t('dashboard.market.btc_oi')}>
            <span className="font-mono">
              {data?.btc_oi_24h_pct != null
                ? formatPct(data.btc_oi_24h_pct, { sign: true, digits: 1 })
                : '—'}
            </span>
          </Row>
          <Row label={t('dashboard.market.cvd_1h')}>
            <span className="font-mono">
              {data?.cvd_1h_usd != null
                ? formatUsd(data.cvd_1h_usd, { compact: true, sign: true })
                : '—'}
            </span>
          </Row>
          <Row label={t('dashboard.market.signals_1h')}>
            <span className="font-mono">{data?.signals_1h_total ?? '—'}</span>
          </Row>
        </dl>
      )}
    </section>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline">
      <dt className="text-textDim">{label}</dt>
      <dd className="ml-auto">{children}</dd>
    </div>
  )
}
